import { Language, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { Option, OptionLanguage } from '@brands/Utils/selectOptions';
import {
  checkFutureDate,
  checkValidDate,
  isValidAddress,
  isValidDate,
  isValidName,
  isValidZipCode,
} from '@brands/Utils/validation';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { MultiValue } from 'react-select/dist/declarations/src/types';
import * as Yup from 'yup';

import { getRoleFormConfig } from './roleForms/roleFormConfig';

export type UserCompleteProfileForm = {
  id?: number;
  first_name: string;
  email: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  date_of_birth: string;
  pronouns?: string;
  gender: string;
  gender_identity: string;
  languages: Language[] | string[];
  language: MultiValue<OptionLanguage> | string[];
  phone_number: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip_code: string;
  d1Code?: string;
  profile_img?: string | null;
  // Role-specific extras (Provider / CareAdvisor first-launch). These are not
  // accepted by /auth/signup and are persisted via a follow-up authenticated
  // save right after OTP/login (see SignUp.tsx).
  education?: string[];
  specialty?: Option[];
  additional_credentials?: string;
  biography?: string;
};

export const validationSchema = (): any => {
  return Yup.object().shape({
    first_name: Yup.string()
      .required('First name is required')
      .test('test-name', 'Enter Valid First Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    middle_name: Yup.string().test('test-name', 'Enter Valid Middle Name', (value) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    last_name: Yup.string()
      .required('Last name is required')
      .test('test-name', 'Enter Valid Last Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    gender: Yup.string()
      .required('Gender is required')
      .test('test-name', 'Gender is required', (value) => {
        if (value === '') {
          return false;
        }
        return true;
      }),
    gender_identity: Yup.string()
      .required('Gender identity is required')
      .test('test-name', 'Gender identity is required', (value) => {
        if (value === '') {
          return false;
        }
        return true;
      }),
    date_of_birth: Yup.string()
      .required('Date of birth is required')
      .test('test-name', 'Valid date: MM/DD/YYYY', (value) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', `DOB can't be in the future`, (value) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    phone_number: Yup.string()
      .required('Phone number is required')
      .test('test-name', 'Enter Valid Phone Number', (value) => {
        if (!value) return false;

        const phoneNumber = parsePhoneNumberFromString(value, 'US');
        return phoneNumber?.isValid() ?? false;
      }),
    language: Yup.array().nullable().min(1, 'Language is required').required('Language is required'),
    address1: Yup.string()
      .required('Address is required')
      .test('test-name', 'Enter Valid Address', (value) => {
        if (value && !isValidAddress(value)) {
          return false;
        }
        return true;
      }),
    address2: Yup.string(),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip_code: Yup.string()
      .required('Zip Code is required')
      .test('test-name', 'Enter Valid Zip Code', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
  });
};

const nameValidator = (label: string, message: string): Yup.StringSchema =>
  Yup.string()
    .required(message)
    .test('test-name', `Enter Valid ${label}`, (value) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    });

const optionalNameValidator = (label: string): Yup.StringSchema =>
  Yup.string().test('test-name', `Enter Valid ${label}`, (value) => {
    if (value && !isValidName(value)) {
      return false;
    }
    return true;
  });

const phoneValidator = (): Yup.StringSchema =>
  Yup.string()
    .required('Phone number is required')
    .test('test-name', 'Enter Valid Phone Number', (value) => {
      if (!value) return false;
      const phoneNumber = parsePhoneNumberFromString(value, 'US');
      return phoneNumber?.isValid() ?? false;
    });

const zipValidator = (): Yup.StringSchema =>
  Yup.string()
    .required('Zip Code is required')
    .test('test-name', 'Enter Valid Zip Code', (value) => {
      if (value && !isValidZipCode(value)) {
        return false;
      }
      return true;
    });

/**
 * Role-aware validation for the first-launch "Complete your profile" form.
 * Each non-patient role only validates the fields shown on its own
 * account-menu my-profile page. Collector is a read-only confirm view and
 * therefore validates nothing. Patient continues to use `validationSchema`.
 */
export const completeProfileValidationSchema = (role?: string): any => {
  const isProvider = role === UserRoleName.Provider;
  const isCareAdvisor = role === UserRoleName.CareAdvisor;
  const isCollector = role === UserRoleName.Collector;

  // Collector just views admin-provided info and confirms.
  if (isCollector) {
    return Yup.object().shape({});
  }

  const addressMode = getRoleFormConfig(role).addressMode;

  const shape: Record<string, Yup.AnySchema> = {
    first_name: nameValidator('First Name', 'First name is required'),
    middle_name: optionalNameValidator('Middle Name'),
    last_name: nameValidator('Last Name', 'Last name is required'),
    phone_number: phoneValidator(),
  };

  if (isProvider) {
    shape.gender = Yup.string().required('Gender is required');
    shape.gender_identity = Yup.string().required('Gender identity is required');
  }

  if (isProvider || isCareAdvisor) {
    shape.language = Yup.array().nullable().min(1, 'Language is required').required('Language is required');
  }

  if (addressMode === 'full') {
    shape.address1 = Yup.string()
      .required('Address is required')
      .test('test-name', 'Enter Valid Address', (value) => {
        if (value && !isValidAddress(value)) {
          return false;
        }
        return true;
      });
    shape.address2 = Yup.string();
    shape.city = Yup.string().required('City is required');
    shape.state = Yup.string().required('State is required');
    shape.zip_code = zipValidator();
  } else if (addressMode === 'zip') {
    shape.zip_code = zipValidator();
  }

  if (isProvider) {
    shape.education = Yup.array()
      .min(1, 'Education / Universities is required')
      .required('Education / Universities is required');
    shape.specialty = Yup.array()
      .min(1, 'Specialty is required')
      .max(6, 'Select up to 6')
      .required('Specialty is required');
    shape.biography = Yup.string().trim().required('Biography is required');
  }

  if (isCareAdvisor) {
    shape.biography = Yup.string().trim().required('Biography is required');
  }

  return Yup.object().shape(shape);
};
